<template>
  <div
    v-if="user"
    class="main-container"
  >
    <a-form
      :model="input"
      @submit="saveChanges()"
    >
      <div class="overall-info">
        <div class="user-profile">
          <div class="img-container">
            <img
              v-if="user.avatarUrl"
              :src="user.avatarUrl"
            >
          </div>
          <div class="title">
            <div v-if="!editMode">
              {{ user.name }} <span style="color:red">*</span>
            </div>
            <a-form-item
              v-else
              name="name"
              :rules="[{ required: true, message: 'Please input name' }]"
            >
              <a-input
                v-model:value="input.name"
              />
            </a-form-item>
          </div>
        </div>
        <div class="subscription-info" />
      </div>

      <div class="details">
        <div class="contact-information">
          <div class="header">
            <div>{{ t('user.contactInformation') }}</div>
            <div
              class="edit"
              @click="editMode = !editMode"
            >
              {{ t('general.edit') }}
            </div>
          </div>
          <div class="info">
            <div class="item">
              <div>{{ t('user.dateOfBirth') }}</div>
              <div v-if="!editMode">
                {{ dateOfBirth }}
              </div>
              <div v-else>
                <datepicker
                  v-model="input.dateOfBirth"
                  :class="['datepicker']"
                  input-format="dd.MM.yyyy"
                  starting-view="year"
                  :upper-limit="new Date()"
                />
              </div>
            </div>
            <div class="item">
              <div>{{ t('user.gender') }}</div>
              <div v-if="!editMode">
                {{ capitalize(user.gender) }}
              </div>
              <a-form-item
                v-else
                name="gender"
              >
                <a-select v-model:value="input.gender">
                  <a-select-option
                    value=""
                    disabled
                  >
                    Gender
                  </a-select-option>
                  <a-select-option
                    v-for="gender in genders"
                    :key="gender.type"
                    :value="gender.type"
                  >
                    {{ gender.name }}
                  </a-select-option>
                </a-select>
              </a-form-item>
            </div>
            <div class="item">
              <div>{{ t('user.contact') }}</div>
              <div>{{ user.email }}</div>
            </div>
            <div class="item">
              <div>{{ t('user.password') }}</div>
              <div class="password">
                <div>********</div>
                <div
                  class="edit"
                  @click="resetPasswordModal.show = !resetPasswordModal.show"
                >
                  {{ t('user.resetPassword') }}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="user-information">
          <div class="header">
            <div>{{ t('user.userInformation') }}</div>
          </div>
          <div class="info">
            <div class="item">
              <div>{{ t('user.institution') }}</div>
              <div>{{ user.institution.name }}</div>
            </div>
            <div class="item">
              <div>{{ t('user.department') }}</div>
              <div>{{ user.departments[0].name }}</div>
            </div>
          </div>
        </div>
      </div>
      <div class="action-buttons">
        <a-button
          v-if="editMode"
          type="primary"
          html-type="submit"
          :disabled="isButtonDisable"
        >
          {{ t('general.save') }}
        </a-button>
        <a-button
          type="danger"
          @click="closeModal(true)"
        >
          {{ t('general.close') }}
        </a-button>
      </div>
    </a-form>
  </div>
  <transition
    name="modal-fade"
    appear
  >
    <ModalWindow
      v-if="resetPasswordModal.show"
      :small-modal="true"
      @close="(type) => resetPasswordModal.show = type"
    >
      <template #title>
        {{ t('user.sendResetPasswordEmailTo') }}: {{ user.email }} ?
      </template>
      <div class="action-buttons">
        <a-button
          type="primary"
          @click="sendResetPassword"
        >
          Yes
        </a-button>
        <a-button
          type="danger"
          @click="resetPasswordModal.show = false"
        >
          No
        </a-button>
      </div>
    </ModalWindow>
  </transition>
</template>

<script>
import { defineComponent } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from '@/store'
import { ActionTypes } from '@/store/actions'
import ModalWindow from '@/components/ModalWindow.vue'
import { useToast } from 'vue-toastification'
import Datepicker from 'vue3-datepicker'

export default defineComponent({
  name: 'UserProfile',
  components: {
    ModalWindow,
    Datepicker
  },
  props: {
    userId: { type: String, default: '' }
  },
  emits: ['close'],
  setup () {
    const { t } = useI18n({
      inheritLocale: true,
      useScope: 'global'
    })

    const toast = useToast()

    const store = useStore()

    const getUser = async (userId) => store.dispatch('users/GET_USER', { userId })
    const resetUserPassword = async (userId) => store.dispatch('users/SEND_RESET_USER_PASSWORD', { userId })
    const updateUser = async (id, userData) => store.dispatch('users/UPDATE_USER', Object.assign({ id }, userData))

    return {
      t,
      toast,
      getUser,
      updateUser,
      resetUserPassword
    }
  },
  data: () => ({
    editMode: false,
    input: {
      gender: '',
      dateOfBirth: '',
      name: ''
    },
    resetPasswordModal: {
      show: false
    },
    user: null,
    genders: [{
      type: 'MALE',
      name: 'Male'
    }, {
      type: 'FEMALE',
      name: 'Female'
    }]
  }),
  computed: {
    dateOfBirth () {
      const date = new Date(this.user.dateOfBirth)
      return `${date.toLocaleString('default', { month: 'short' })} ${date.getDate()}, ${date.getFullYear()}`
    },
    isButtonDisable () {
      return this.input.name === ''
    }
  },
  async mounted () {
    const user = await this.getUser(this.userId)
    this.user = user
    this.input.name = user.name
    this.input.gender = user.gender
    this.input.dateOfBirth = new Date(user.dateOfBirth)
  },
  methods: {
    async saveChanges () {
      const user = await this.updateUser(this.user.id, this.input)
      this.user = user
      this.toast.success(this.t('user.userDetailsUpdated'))
      this.editMode = false
    },
    closeModal (type) {
      this.$emit('close', type || true)
    },
    async sendResetPassword () {
      const result = await this.resetUserPassword(this.user.id)
      if (result) {
        this.toast.success(`${this.t('user.resetRequestSentTo')}: ${this.user.email}`)
      }
      this.resetPasswordModal.show = false
    },
    capitalize (value) {
      if (!value) return ''

      value = value.toString().toLowerCase()
      return value.charAt(0).toUpperCase() + value.slice(1)
    }
  }
})
</script>

<style lang="scss" scoped>
.input, select, textarea {
  font-size: 14px;
  line-height: 30px;
  letter-spacing: 0.15px;
  mix-blend-mode: normal;
  padding: 0 10px;
  height: 30px;
  background: #FFFFFF;
  border: 1px solid #DFE0EB;
  border-radius: 8px;
  width: 100%;
  color: black;

  &::placeholder {
    color: #DFE0EB;
  }
}

.header {
  font-weight: normal;
  font-size: 16px;
  line-height: 30px;
  letter-spacing: 0.15px;
  color: #666666;
  mix-blend-mode: normal;
  border-bottom: 1px solid #C5C7CD;
  width: 100%;
  text-transform: uppercase;
  text-align: start;

  .edit {
    text-transform: none;
    cursor: pointer;
  }
}

.main-container {
  margin-top: 30px;
  display: flex;
  flex-direction: column;

  .overall-info {
    justify-content: space-between;
    display: flex;
    flex-direction: row;
    margin-bottom: 30px;

    .user-profile {
      width: 50%;
      margin-right: 10px;
      display: flex;

      .img-container {
        margin-right: 40px;
        border-radius: 50%;

        img {
          width: 99px;
          border-radius: 50%;
        }
      }

      .title {
        font-size: 20px;
        font-weight: 500;
      }
    }

    .subscription-info {
      width: 50%;
      margin-left: 10px;
    }
  }

  .details {
    justify-content: space-between;
    display: flex;
    flex-direction: row;

    .info {
      margin-top: 20px;
      display: flex;
      flex-direction: column;

      .item {
        display: flex;
        flex-direction: row;
        line-height: 30px;
        font-size: 16px;
        color: #666666;

        div {
          width: 50%;
        }

        .password {
          .edit {
            font-size: 12px;
            cursor: pointer;
          }
        }
      }
    }

    .contact-information {
      width: 50%;
      margin-right: 30px;

      .header {
        position: relative;
        display: flex;
        justify-content: space-between;
        align-items: center;
      }

    }

    .user-information {
      width: 50%;
      margin-left: 30px;

      .header {
        position: relative;
        display: flex;
        justify-content: space-between;
        align-items: center;
      }
    }
  }
}

.action-buttons {
    display: flex;
    width: 100%;
    justify-content: flex-end;
    align-items: center;
    margin-top: 30px;

    button {
      height: 36px;
      width: 174px;
      border-radius: 20px;
      margin-left: 19px;
      font-weight: 600;
      font-size: 17px;
      line-height: 20px;
      border: none;
      text-align: center;
      letter-spacing: -0.24px;
      color: #FFFFFF;
    }
  }
</style>
